import { useId, useRef } from 'react';
import {
  type NativeSyntheticEvent,
  StyleSheet,
  TextInput,
  type TextInputKeyPressEventData,
  View,
} from 'react-native';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { darkPrecisionFontFamilies } from '../../../theme/typography';

interface OtpInputProps {
  value: string;
  onChange: (next: string) => void;
  length?: number;
  editable?: boolean;
  autoFocus?: boolean;
  testID?: string;
  accessibilityLabel?: string;
  accessibilityLabelledBy?: string;
}

function replaceAt(value: string, index: number, char: string): string {
  const chars = value.split('');
  while (chars.length <= index) {
    chars.push('');
  }
  chars[index] = char;
  return chars.join('');
}

export function OtpInput({
  value,
  onChange,
  length = 6,
  editable = true,
  autoFocus = false,
  testID = 'otp-input',
  accessibilityLabel,
  accessibilityLabelledBy,
}: Readonly<OtpInputProps>) {
  const refs = useRef<Array<TextInput | null>>([]);
  const groupId = useId();
  const slotKeys = Array.from({ length }, (_, index) => `${groupId}-${index}`);

  const focus = (index: number) => {
    if (index >= 0 && index < length) {
      refs.current[index]?.focus();
    }
  };

  const handleChange = (index: number, text: string) => {
    if (text.length === 0) {
      if (value[index]) {
        // This box had a digit (e.g. select-all delete) — drop it in place.
        onChange(replaceAt(value, index, '').slice(0, length));
        return;
      }
      // Empty change on an already-empty box = Backspace. Android does not
      // reliably emit onKeyPress for Backspace, so treat it as backward nav
      // here; iOS/web also hit onKeyPress, but both clear index-1 idempotently.
      if (index > 0) {
        onChange(replaceAt(value, index - 1, '').slice(0, length));
        focus(index - 1);
      }
      return;
    }
    const digits = text.replace(/\D/g, '');
    if (digits.length === 0) {
      // Non-numeric keystroke — ignore.
      return;
    }
    if (digits.length > 1) {
      // Paste or platform OTP autofill landed the whole code in one box.
      const next = (value.slice(0, index) + digits).slice(0, length);
      onChange(next);
      focus(Math.min(index + digits.length, length - 1));
      return;
    }
    onChange(replaceAt(value, index, digits).slice(0, length));
    focus(index + 1);
  };

  const handleKeyPress = (
    index: number,
    event: NativeSyntheticEvent<TextInputKeyPressEventData>,
  ) => {
    if (event.nativeEvent.key === 'Backspace' && !value[index] && index > 0) {
      onChange(replaceAt(value, index - 1, '').slice(0, length));
      focus(index - 1);
    }
  };

  return (
    <View style={styles.row} accessibilityRole="none">
      {Array.from({ length }, (_, index) => {
        const filled = Boolean(value[index]);
        return (
          <TextInput
            key={slotKeys[index]}
            ref={(el) => {
              refs.current[index] = el;
            }}
            testID={index === 0 ? testID : `${testID}-${index}`}
            value={value[index] ?? ''}
            onChangeText={(text) => handleChange(index, text)}
            onKeyPress={(event) => handleKeyPress(index, event)}
            editable={editable}
            autoFocus={index === 0 ? autoFocus : undefined}
            keyboardType="number-pad"
            maxLength={index === 0 ? length : 1}
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            textContentType={index === 0 ? 'oneTimeCode' : 'none'}
            selectTextOnFocus
            accessibilityLabel={
              index === 0 ? accessibilityLabel : `Digit ${index + 1} of ${length}`
            }
            accessibilityLabelledBy={index === 0 ? accessibilityLabelledBy : undefined}
            style={[styles.box, filled && styles.boxFilled, !editable && styles.boxDisabled]}
          />
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: tokens.spacing.sm,
    justifyContent: 'center',
  },
  box: {
    width: 46,
    height: 56,
    textAlign: 'center',
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.input,
    backgroundColor: colors.background.input,
    color: colors.text.primary,
    fontFamily: darkPrecisionFontFamilies.metricBold,
    fontSize: tokens.fontSize.xlPlus,
    fontWeight: '600',
  },
  boxFilled: {
    borderColor: colors.border.focus,
  },
  boxDisabled: {
    opacity: 0.6,
  },
});
